import React, { useState } from 'react';

import { App } from '@flipdish/api-client-typescript';
import { Button } from '@flipdish/ui-library/components/atoms/Button';
import MuiDialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { Translate } from 'react-localize-redux';
import Permissions from 'react-redux-permissions';

type Props = {
  handleRemove: () => Promise<void>;
  storeNames?: string[];
};

const RemoveAccountButtonInner = ({ handleRemove, storeNames }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasStores = storeNames && storeNames.length > 0;

  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      await handleRemove();
      setIsOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button
        color="secondary-destructive"
        size="md"
        data-fd="remove-account"
        onPress={() => setIsOpen(true)}
        className="w-full sm:w-auto"
      >
        <Translate id="Remove_account" />
      </Button>

      <MuiDialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        aria-labelledby="Delete_bank_account"
      >
        <DialogTitle id="Delete_bank_account">
          <Translate id="Delete_bank_account" />
        </DialogTitle>

        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body1" color="textSecondary">
              {hasStores ? (
                <Translate id="bank_account_is_currently_assigned" />
              ) : (
                <Translate id="Pressing_delete_will_remove" />
              )}
            </Typography>

            {hasStores && (
              <List dense>
                {storeNames!.map((storeName, index) => (
                  <ListItem disableGutters key={index}>
                    <ListItemText primary={storeName} />
                  </ListItem>
                ))}
              </List>
            )}

            <Typography variant="body1" color="textSecondary">
              {hasStores ? (
                <Translate id="cannot_transfer_money_until_bank_account_reassigned" />
              ) : (
                <Translate id="this_bank_account_for_good" />
              )}
            </Typography>
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            color="secondary"
            size="md"
            data-fd="cancel-remove-account"
            isDisabled={isSubmitting}
            onPress={() => setIsOpen(false)}
          >
            <Translate id="Cancel" />
          </Button>
          <Button
            color="secondary-destructive"
            size="md"
            data-fd="confirm-delete-account"
            isDisabled={isSubmitting}
            onPress={handleDelete}
          >
            <Translate id="Delete" />
          </Button>
        </DialogActions>
      </MuiDialog>
    </>
  );
};

const RemoveAccountButton = (props: Props) => (
  <Permissions allowed={[App.AppResourceSetEnum.UpdateBankAccounts]}>
    <RemoveAccountButtonInner {...props} />
  </Permissions>
);

export default RemoveAccountButton;
